import type { UserAddress } from "@shared/schemas";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { useTranslation } from "react-i18next";
import {
  setPreferencesViewBillingAddressCardTestId,
  setPreferencesViewBillingAddressDescriptionTestId,
  setPreferencesViewBillingAddressTitleTestId,
} from "../views/preferences-view.utils";
import { RequiredAsterisk } from "./RequiredAsterisk";

export interface BillingAddressCardProps {
  billingAddress?: UserAddress;
}

const cardTestId = setPreferencesViewBillingAddressCardTestId();
const titleTestId = setPreferencesViewBillingAddressTitleTestId();
const descriptionTestId = setPreferencesViewBillingAddressDescriptionTestId();

const getBillingValue = (value: string | undefined) => value ?? "";

export const BillingAddressCard = ({ billingAddress }: BillingAddressCardProps) => {
  const { t } = useTranslation();

  return (
    <Card data-testid={cardTestId}>
      <CardHeader>
        <CardTitle data-testid={titleTestId}>{t("views.preferences.billingAddress.title")}</CardTitle>
        <CardDescription data-testid={descriptionTestId}>{t("views.preferences.billingAddress.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2">
          <Label htmlFor="billing-street">
            {t("views.preferences.shippingAddress.street")}
            <RequiredAsterisk />
          </Label>
          <Input id="billing-street" value={getBillingValue(billingAddress?.street)} disabled readOnly />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="billing-city">
              {t("views.preferences.shippingAddress.city")}
              <RequiredAsterisk />
            </Label>
            <Input id="billing-city" value={getBillingValue(billingAddress?.city)} disabled readOnly />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="billing-country">
              {t("views.preferences.shippingAddress.country")}
              <RequiredAsterisk />
            </Label>
            <Input id="billing-country" value={getBillingValue(billingAddress?.country)} disabled readOnly />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="billing-zip">
              {t("views.preferences.shippingAddress.zip")}
              <RequiredAsterisk />
            </Label>
            <Input id="billing-zip" value={getBillingValue(billingAddress?.postalCode)} disabled readOnly />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="billing-state">
              {t("views.preferences.shippingAddress.state")}
              {billingAddress?.state && <RequiredAsterisk />}
            </Label>
            <Input id="billing-state" value={getBillingValue(billingAddress?.state)} disabled readOnly />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
